import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { formatPoints } from "src/utils/ranking"
import { avatarFor } from "src/utils/cosmetics"
import { avatars } from "src/assets/images"
import LeagueFrame from "src/components/LeagueFrame"

/* The home page's podium, which no longer stops at three (owner, 2026-09-29), and which no longer
   steps (owner, 2026-10-04: *"starts from right (0 players visible), and slowly keeps moving to
   the left so we have time to see top 1"*). The row opens empty, the leader enters from the right
   edge first, and the whole list (the top 100 the server sends) drifts left at one steady pace
   until the last card has left; then it comes in from the right again. A step that moved the
   leader out of view two seconds after the page opened was a ranking nobody could read the top
   of.

   The pace is a card's width every `CARD_MS`, measured off the window, so a card is in view for
   the same time at every width. It keeps moving under the pointer (owner, 2026-10-09: stopping
   on hover was "weird"), and holds still only while the keyboard is on a card, so a card focused
   off-screen is brought into view and stays there to be read. With three players or fewer, or under "reduce motion", the row stands
   still with the leader first.

   The offset is written straight onto the track every frame rather than through React state: a
   re-render of a hundred cards sixty times a second is the cost the DOM write avoids. */
export const VISIBLE = 3
export const CARD_MS = 2500
const GAP_PX = 12

// One frame of the drift: `offset` is where the track's left edge stands against the window's,
// so it starts at the window's width (nothing in view) and ends past minus the track's width.
export const nextOffset = ({ offset, elapsedMs, windowWidth, trackWidth }) => {
  const perMs = (windowWidth + GAP_PX) / VISIBLE / CARD_MS
  const next = offset - perMs * elapsedMs
  return next < -trackWidth ? windowWidth : next
}

const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches === true

const HallOfFame = ({ users, onPick }) => {
  const { t } = useTranslation()
  const [held, setHeld] = useState(false)
  const windowRef = useRef(null)
  const trackRef = useRef(null)
  const offsetRef = useRef(null)
  const moves = users.length > VISIBLE && !prefersReducedMotion()

  useEffect(() => {
    const track = trackRef.current
    const frame = windowRef.current
    if (!track || !frame) return undefined
    if (!moves) {
      offsetRef.current = 0
      track.style.transform = "translateX(0px)"
      return undefined
    }
    if (offsetRef.current === null) offsetRef.current = frame.clientWidth
    track.style.transform = `translateX(${offsetRef.current}px)`
    if (held) return undefined

    let last = null
    let request = null
    const step = (now) => {
      if (last !== null) {
        offsetRef.current = nextOffset({
          offset: offsetRef.current,
          elapsedMs: Math.min(now - last, 100),
          windowWidth: frame.clientWidth,
          trackWidth: track.scrollWidth,
        })
        track.style.transform = `translateX(${offsetRef.current}px)`
      }
      last = now
      request = window.requestAnimationFrame(step)
    }
    request = window.requestAnimationFrame(step)
    return () => window.cancelAnimationFrame(request)
  }, [moves, held, users.length])

  // A card focused from the keyboard is brought into view, at the left edge, before it is read.
  const reveal = (event) => {
    setHeld(true)
    const card = event.target.closest?.(".hall-card")
    const track = trackRef.current
    const frame = windowRef.current
    if (!moves || !card || !track || !frame) return
    const left = card.offsetLeft + offsetRef.current
    if (left < 0 || left + card.offsetWidth > frame.clientWidth) {
      offsetRef.current = -card.offsetLeft
      track.style.transform = `translateX(${offsetRef.current}px)`
    }
  }

  return (
    <div
      className="hall-cards"
      data-testid="hall-cards"
      ref={windowRef}
      onFocus={reveal}
      onBlur={() => setHeld(false)}
    >
      <div className={`hall-track${moves ? " drifting" : ""}`} data-testid="hall-track" ref={trackRef}>
        {users.map((user, position) => (
          <button
            type="button"
            key={user.id ?? user.username}
            className={`hall-card${position === 0 ? " leader" : ""}`}
            onClick={() => onPick(user)}
            aria-label={t("home.hallCardLabel", { rank: position + 1, name: user.username })}
          >
            {/* The player's picture in the frame of their league (owner, 2026-10-08), the same
                frame their lobby seat wears, with the league's name on its plate (owner,
                2026-10-09). */}
            <LeagueFrame league={user.league} size="sm" className="hall-frame">
              <span className="hall-look">
                <img src={avatars[avatarFor(user)]} alt="" />
              </span>
            </LeagueFrame>
            <span className="hall-text">
              <span className="hall-rank">{String(position + 1).padStart(2, "0")}</span>
              <span className="hall-name">{user.username}</span>
              <span className="hall-points">{t("home.points", { points: formatPoints(user.rankingPoints) })}</span>
            </span>
          </button>
        ))}
      </div>
    </div>
  )
}

export default HallOfFame
